import type { FC, ReactNode } from 'react'
import type { NavItem } from '@/types'

import { Rocket } from 'lucide-react'
import { NewsletterForm } from '@/components/kit/newsletter-form'
import { PageLink } from '@/components/kit/page-link'
import { PlatformIcon } from '@/components/kit/platform-icon'

import {
    footerLegal,
    footerPages,
    footerStudio,
    siteConfig,
    TAGLINE
} from '@/constants/site'

const columnTitle = 'font-mono text-[11px] uppercase tracking-[0.16em] text-stone'

const FooterColumn: FC<{ title: string, links: Array<NavItem> }> = ({ title, links }) => {
    return (
        <div>
            <p className={columnTitle}>{title}</p>

            <ul className='mt-5 flex flex-col gap-2.5'>
                {links.map(link => (
                    <li key={link.href}>
                        <PageLink
                            href={link.href}
                            className='group inline-flex items-center gap-2 text-[15px] text-ink/80 transition-colors hover:text-ink'
                        >
                            <span className='h-px w-0 bg-ember transition-[width] duration-500 ease-expo group-hover:w-3' />
                            {link.label}
                        </PageLink>
                    </li>
                ))}
            </ul>
        </div>
    )
}

export const Footer: FC = (): ReactNode => {
    const toTop = () => {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' })
    }

    return (
        <footer className='relative overflow-hidden pt-24 md:pt-32'>
            <div className='mx-auto max-w-[1400px] px-5 md:px-10'>
                <div className='rule-double flex flex-wrap items-center justify-between gap-3 pt-3 font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>
                    <span>The back page</span>
                    <span>{TAGLINE}</span>
                    <span className='hidden sm:inline'>London, cloudy with a chance of virality</span>
                </div>

                <div className='mt-12 grid gap-16 lg:grid-cols-12 lg:gap-10'>
                    <div className='lg:col-span-5'>
                        <div className='relative rounded-[1.75rem] border border-ink/10 bg-paper p-7 shadow-[0_24px_50px_-36px_rgb(30_30_30/0.45)] md:p-9'>
                            <span
                                aria-hidden='true'
                                className='tape -top-3 left-10 -rotate-6'
                            />

                            <div className='flex items-start justify-between gap-4'>
                                <p className={columnTitle}>Newsletter, every Tuesday</p>
                                <span className='stamp -rotate-6 text-ember'>Free</span>
                            </div>

                            <h2 className='mt-5 text-[clamp(2.25rem,4.2vw,3.4rem)] font-bold leading-[0.95] tracking-[-0.04em]'>
                                The Tuesday <span className='font-serif font-normal italic tracking-[-0.01em]'>Signal</span>
                            </h2>

                            <p className='mt-4 max-w-sm text-base leading-relaxed text-stone'>
                                One email a week. Three things worth stealing. No fluff.
                            </p>

                            <NewsletterForm
                                source='footer'
                                className='mt-7'
                            />
                        </div>
                    </div>

                    <div className='grid grid-cols-2 gap-x-8 gap-y-12 sm:grid-cols-4 lg:col-span-7'>
                        <FooterColumn
                            title='Pages'
                            links={footerPages}
                        />

                        <FooterColumn
                            title='Studio'
                            links={footerStudio}
                        />

                        <FooterColumn
                            title='Legal'
                            links={footerLegal}
                        />

                        <div>
                            <p className={columnTitle}>Socials</p>

                            <ul className='mt-5 flex flex-col gap-2.5'>
                                {siteConfig.socials.map(social => (
                                    <li key={social.platform}>
                                        <a
                                            href={social.href}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            className='group inline-flex items-center gap-2.5 text-[15px] text-ink/80 transition-colors hover:text-ink'
                                        >
                                            <PlatformIcon
                                                name={social.platform}
                                                className='size-4 text-stone transition-colors group-hover:text-ember'
                                            />
                                            {social.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className='col-span-2 flex flex-col gap-1.5 border-t border-ink/10 pt-6 text-[15px] sm:col-span-4'>
                            <a
                                href={`mailto:${siteConfig.email}`}
                                className='font-heading text-2xl font-bold tracking-[-0.03em] text-ink underline decoration-ember decoration-2 underline-offset-[6px]'
                            >
                                {siteConfig.email}
                            </a>
                            <p className='text-stone'>
                                {siteConfig.address}. We reply within 24 hours, usually faster.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div
                aria-hidden='true'
                className='mt-20 select-none px-3 pt-[3vw] md:mt-28 md:px-6'
            >
                <div className='flex justify-between font-heading text-[min(19vw,22rem)] font-bold uppercase leading-[0.78] tracking-[-0.045em] text-ink'>
                    {'Updraft'.split('').map((letter, index) => (
                        <span
                            key={`${letter}-${index}`}
                            className='lift-letter'
                        >
                            {letter}
                        </span>
                    ))}
                </div>
            </div>

            <div className='mx-auto mt-8 flex max-w-[1400px] flex-col items-start justify-between gap-5 border-t border-ink/10 px-5 py-6 sm:flex-row sm:items-center md:px-10'>
                <p className='font-mono text-xs text-stone'>
                    © 2026 Updraft Studio.
                </p>

                <p className='font-mono text-xs text-stone'>
                    Made with too much coffee and a very good group chat.
                </p>

                <button
                    type='button'
                    onClick={toTop}
                    aria-label='Back to top'
                    data-cursor='Lift off'
                    className='group stamp-round size-20 shrink-0 -rotate-12 flex-col gap-0.5 text-ember transition-transform duration-500 ease-expo hover:-translate-y-1 hover:rotate-0'
                >
                    <Rocket className='size-5 transition-transform duration-500 ease-expo group-hover:-translate-y-1' />
                    <span className='text-[8.5px] tracking-[0.18em]'>To the top</span>
                </button>
            </div>
        </footer>
    )
}
